import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiTrash2, FiSearch, FiUpload, FiDownload, FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { today, formatDate, currentMonth, unwrapList } from '../../utils/format';
import SearchableSelect from '../../components/common/SearchableSelect';

const STATUSES = ['present', 'late', 'absent', 'half_day', 'leave', 'off'];

export default function Attendance() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', month: currentMonth(), per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulk, setBulk] = useState(false);
  const [bulkDate, setBulkDate] = useState(today());
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [importModal, setImportModal] = useState(false);
  const [importDate, setImportDate] = useState(today());
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState('');
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.attendance.list(params);
      const list = Array.isArray(data.data) ? data.data : data.attendance || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load attendance', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const openBulk = async () => {
    try {
      const { data } = await api.employees.list({ per_page: 500 });
      const emps = unwrapList(data, 'employees');
      setRecords(emps.map((e) => ({ employee_id: e.id, employee_name: e.name, status: 'present', late_minutes: '', overtime_minutes: '', remarks: '' })));
      setErrors({});
      setBulk(true);
    } catch (err) {
      showError('Failed to load employees', extractError(err));
    }
  };

  const updateRecord = (i, field, value) => {
    const next = [...records];
    next[i] = { ...next[i], [field]: value };
    setRecords(next);
  };

  const handleBulkSave = async () => {
    const errs = {};
    if (!bulkDate) errs.date = 'Date is required';
    if (!records.length) errs.records = 'At least one employee is required';
    if (records.some((r) => !r.employee_id || r.late_minutes === '' || r.late_minutes == null || !r.remarks)) errs.records = 'Fill late minutes and remarks for every row';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      await api.attendance.bulk({
        date: bulkDate,
        records: records.map((r) => ({
          employee_id: r.employee_id,
          status: r.status,
          late_minutes: r.late_minutes ? Number(r.late_minutes) : undefined,
          overtime_minutes: r.overtime_minutes ? Number(r.overtime_minutes) : undefined,
          remarks: r.remarks || undefined,
        })),
      });
      toast('success', '', 'Attendance saved');
      setBulk(false);
      load();
    } catch (err) {
      showError('Failed to save attendance', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete('this attendance record');
    if (!res.isConfirmed) return;
    try {
      await api.attendance.remove(row.id);
      toast('success', '', 'Record deleted');
      load();
    } catch (err) {
      showError('Failed to delete record', extractError(err));
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.attendance.template({ date: importDate });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-template-${importDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError('Failed to download template', extractError(err));
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportErr('Select a CSV file first');
      return;
    }
    const fd = new FormData();
    fd.append('file', importFile);
    fd.append('date', importDate);
    setImporting(true);
    setImportErr('');
    try {
      const { data } = await api.attendance.importCsv(fd);
      setImportResult({ message: data.message, saved: data.saved, error_count: data.error_count, errors: data.errors || [] });
      toast('success', '', data.message || 'Import completed');
      load();
    } catch (err) {
      showError('Import failed', extractError(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Attendance"
        subtitle="Daily attendance marking and summary."
        crumbs={[{ label: 'HR' }, { label: 'Attendance' }]}
        actions={hasPermission('create-attendance') && (
          <>
            <button className="btn btn-secondary" onClick={() => { setImportModal(true); setImportFile(null); setImportErr(''); setImportResult(null); }}>
              <FiUpload /> Import CSV
            </button>
            <button className="btn btn-primary" onClick={openBulk}><FiPlus /> Mark Attendance</button>
          </>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search employee..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
            <input className="form-control" type="month" value={params.month} onChange={(e) => setParams({ ...params, month: e.target.value, page: 1 })} />
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="attendance" label="attendance" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No attendance records"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
              { key: 'employee', label: 'Employee', render: (r) => (r.employee ? r.employee.name : '-') },
              { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              { key: 'late_minutes', label: 'Late (min)' },
              { key: 'overtime_minutes', label: 'Overtime (min)' },
              { key: 'remarks', label: 'Remarks' },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('delete-attendance') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal open={bulk} onClose={() => { setErrors({}); setBulk(false); }} title="Mark Attendance" size="xl"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setBulk(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleBulkSave} disabled={saving}>{saving ? 'Saving...' : 'Save All'}</button>
        </>}
      >
        <div className="filters-bar">
          <label className="form-label" style={{ margin: 0 }}>Date:</label>
          <input className={`form-control ${errors.date ? 'is-invalid' : ''}`} type="date" value={bulkDate} onChange={(e) => { setBulkDate(e.target.value); if (errors.date) setErrors((p) => ({ ...p, date: '' })); }} />
          {errors.date && <div className="invalid-feedback">{errors.date}</div>}
        </div>
        {errors.records && <div className="invalid-feedback" style={{ marginBottom: 10 }}>{errors.records}</div>}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Employee</th><th style={{ width: 150 }}>Status</th><th style={{ width: 100 }}>Late (min)</th><th style={{ width: 100 }}>Overtime (min)</th><th>Remarks</th></tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r.employee_id}>
                  <td>{r.employee_name}</td>
                  <td>
                    <SearchableSelect options={STATUSES} value={r.status} onChange={(v) => updateRecord(i, 'status', v)} placeholder="Status" />
                  </td>
                  <td><input className="form-control" type="number" value={r.late_minutes} onChange={(e) => updateRecord(i, 'late_minutes', e.target.value)} /></td>
                  <td><input className="form-control" type="number" value={r.overtime_minutes} onChange={(e) => updateRecord(i, 'overtime_minutes', e.target.value)} placeholder="0" /></td>
                  <td><input className="form-control" value={r.remarks} onChange={(e) => updateRecord(i, 'remarks', e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>

      <Modal open={importModal} onClose={() => setImportModal(false)} title="Import Attendance from CSV" size="md"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setImportModal(false)}>Close</button>
            <button className="btn btn-primary" onClick={handleImport} disabled={importing || !importFile}>
              {importing ? <><FiLoader className="spin" /> Importing...</> : <><FiUpload /> Import File</>}
            </button>
          </>
        }
      >
        <div className="alert-banner" style={{ marginBottom: 14 }}>
          Download the template, fill it with employee numbers and statuses, then upload the CSV.
          Same date + employee = existing record gets updated.
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Attendance Date <span className="req">*</span></label>
            <input className="form-control" type="date" value={importDate} onChange={(e) => setImportDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">&nbsp;</label>
            <button className="btn btn-secondary" onClick={downloadTemplate} style={{ width: '100%' }}>
              <FiDownload /> Download Template
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">CSV File <span className="req">*</span></label>
          <input
            className={`form-control ${importErr ? 'is-invalid' : ''}`}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => { setImportFile(e.target.files[0] || null); setImportErr(''); setImportResult(null); }}
          />
          {importErr && <div className="invalid-feedback">{importErr}</div>}
        </div>

        {importResult && (
          <div className={`alert-banner ${importResult.error_count > 0 ? 'warn' : ''}`} style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: 'var(--success)' }}>
                <FiCheckCircle /> {importResult.saved} saved
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700, color: 'var(--danger)' }}>
                <FiXCircle /> {importResult.error_count} errors
              </span>
            </div>
            {importResult.errors && importResult.errors.length > 0 && (
              <ul style={{ margin: '10px 0 0 18px', fontSize: 12.5 }}>
                {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}